/**
 * TrainingSettingsPanel — Impostazioni → Formazione (solo OPS).
 * Gestione completa della sezione: tab visibili/rinominabili, tab personalizzati,
 * titolo/sottotitolo pagina, tipologie formazione, legenda percorso (descrizioni),
 * accesso rapido all'editor dei template delle schede.
 */
import React, { useEffect, useState } from 'react';
import { trainingApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ClipboardList, Loader2, Plus, Save, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import TemplateEditorDialog from './training/TemplateEditorDialog';

const TAB_DEFS = [
  { key: 'registers', def: 'Registri formativi' },
  { key: 'evaluations', def: 'Schede di valutazione' },
  { key: 'manual', def: 'Manuale' },
  { key: 'videos', def: 'Video tutorial' },
  { key: 'documents', def: 'Documenti' },
];

const Section = ({ title, hint, children, testId }) => (
  <div className="bg-white border border-slate-200 p-4" data-testid={testId}>
    <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h3>
    {hint && <p className="text-xs text-slate-400 mt-0.5 mb-3">{hint}</p>}
    {!hint && <div className="mb-3" />}
    {children}
  </div>
);

export default function TrainingSettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [newType, setNewType] = useState('');

  useEffect(() => {
    trainingApi.getSettings()
      .then(r => setCfg(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Errore caricamento impostazioni Formazione'));
  }, []);

  if (!cfg) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const setTab = (key, patch) => setCfg(c => ({ ...c, tabs: { ...c.tabs, [key]: { ...c.tabs[key], ...patch } } }));
  const setArea = (i, patch) => setCfg(c => ({ ...c, areas: c.areas.map((a, j) => j === i ? { ...a, ...patch } : a) }));
  const setCustom = (i, patch) => setCfg(c => ({ ...c, custom_tabs: c.custom_tabs.map((t, j) => j === i ? { ...t, ...patch } : t) }));

  const addType = () => {
    const t = newType.trim();
    if (!t) return;
    if (cfg.types.includes(t)) { toast.error('Tipologia già presente'); return; }
    setCfg(c => ({ ...c, types: [...c.types, t] }));
    setNewType('');
  };

  const save = async () => {
    if (!cfg.page_title.trim()) { toast.error('Il titolo della pagina è obbligatorio'); return; }
    if (cfg.types.length === 0) { toast.error('Serve almeno una tipologia di formazione'); return; }
    if ((cfg.custom_tabs || []).some(t => !t.label.trim())) { toast.error('Ogni tab personalizzato deve avere un nome'); return; }
    if ((cfg.areas || []).some(a => !a.name.trim())) { toast.error('Ogni area della legenda deve avere un nome'); return; }
    setSaving(true);
    try {
      const r = await trainingApi.updateSettings({
        page_title: cfg.page_title.trim(),
        page_subtitle: cfg.page_subtitle || '',
        tabs: cfg.tabs,
        custom_tabs: (cfg.custom_tabs || []).map(t => ({ tab_id: t.tab_id || null, label: t.label.trim(), content: t.content || '' })),
        types: cfg.types,
        areas: (cfg.areas || []).map(a => ({ name: a.name.trim(), hours: parseFloat(a.hours) || 0, category: a.category || 'tecnica', description: a.description || '' })),
      });
      setCfg(r.data);
      toast.success('Impostazioni Formazione salvate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio fallito');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="training-settings-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">
          Gestione completa della sezione Formazione: tab, testi, tipologie e legenda percorso.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-9 gap-1.5 rounded-none" onClick={() => setShowTemplates(true)}
                  data-testid="training-settings-templates-btn">
            <ClipboardList className="w-4 h-4" /> Template schede di valutazione
          </Button>
          <Button size="sm" className="h-9 gap-1.5 rounded-none" onClick={save} disabled={saving} data-testid="training-settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
          </Button>
        </div>
      </div>

      <Section title="Testi pagina" testId="training-settings-texts">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Titolo pagina</label>
            <Input value={cfg.page_title} onChange={e => setCfg(c => ({ ...c, page_title: e.target.value }))}
                   maxLength={80} className="rounded-none mt-1" data-testid="training-settings-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Sottotitolo</label>
            <Input value={cfg.page_subtitle || ''} onChange={e => setCfg(c => ({ ...c, page_subtitle: e.target.value }))}
                   maxLength={200} className="rounded-none mt-1" data-testid="training-settings-subtitle" />
          </div>
        </div>
      </Section>

      <Section title="Tab della sezione" hint="Mostra/nascondi e rinomina i tab standard." testId="training-settings-tabs">
        <div className="space-y-2">
          {TAB_DEFS.map(t => (
            <div key={t.key} className="flex items-center gap-3">
              <Switch checked={cfg.tabs?.[t.key]?.visible !== false}
                      onCheckedChange={v => setTab(t.key, { visible: v })}
                      data-testid={`training-settings-tab-visible-${t.key}`} />
              <Input value={cfg.tabs?.[t.key]?.label ?? t.def}
                     onChange={e => setTab(t.key, { label: e.target.value })}
                     maxLength={60} className="h-8 rounded-none max-w-xs"
                     data-testid={`training-settings-tab-label-${t.key}`} />
              <span className="text-[10px] uppercase tracking-wider text-slate-400">{t.def}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Tab personalizzati" hint="Tab aggiuntivi con contenuto testuale libero (visibili a tutti nella sezione Formazione)."
               testId="training-settings-custom-tabs">
        <div className="space-y-3">
          {(cfg.custom_tabs || []).map((t, i) => (
            <div key={t.tab_id || i} className="border border-slate-200 p-3 space-y-2 bg-slate-50/60" data-testid={`training-settings-custom-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={t.label} onChange={e => setCustom(i, { label: e.target.value })}
                       placeholder="Nome del tab" maxLength={60} className="h-8 rounded-none max-w-xs bg-white"
                       data-testid={`training-settings-custom-${i}-label`} />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 ml-auto"
                        onClick={() => setCfg(c => ({ ...c, custom_tabs: c.custom_tabs.filter((_, j) => j !== i) }))}
                        data-testid={`training-settings-custom-${i}-remove`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Textarea value={t.content || ''} onChange={e => setCustom(i, { content: e.target.value })}
                        rows={4} placeholder="Contenuto del tab (testo libero)" className="rounded-none text-sm bg-white"
                        data-testid={`training-settings-custom-${i}-content`} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="h-8 gap-1 rounded-none text-xs"
                  onClick={() => setCfg(c => ({ ...c, custom_tabs: [...(c.custom_tabs || []), { tab_id: null, label: '', content: '' }] }))}
                  data-testid="training-settings-add-custom-tab">
            <Plus className="w-3.5 h-3.5" /> Aggiungi tab personalizzato
          </Button>
        </div>
      </Section>

      <Section title="Tipologie formazione erogata" hint="Le voci selezionabili nei moduli dei registri formativi."
               testId="training-settings-types">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {cfg.types.map(t => (
            <span key={t} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-slate-900 text-white"
                  data-testid={`training-settings-type-${t.toLowerCase().replace(/ /g, '-')}`}>
              {t}
              <button type="button" className="text-slate-400 hover:text-white"
                      onClick={() => setCfg(c => ({ ...c, types: c.types.filter(x => x !== t) }))}
                      data-testid={`training-settings-type-remove-${t.toLowerCase().replace(/ /g, '-')}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input value={newType} onChange={e => setNewType(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addType(); } }}
                 placeholder="Nuova tipologia…" maxLength={60} className="h-8 rounded-none max-w-xs"
                 data-testid="training-settings-new-type" />
          <Button variant="outline" size="sm" className="h-8 rounded-none gap-1 text-xs" onClick={addType}
                  data-testid="training-settings-add-type">
            <Plus className="w-3.5 h-3.5" /> Aggiungi
          </Button>
        </div>
      </Section>

      <Section title="Legenda percorso (aree tematiche)"
               hint="Le aree numerate a cui fa riferimento il n° modulo. La descrizione appare al passaggio del mouse nella legenda."
               testId="training-settings-areas">
        <div className="space-y-3">
          {(cfg.areas || []).map((a, i) => (
            <div key={i} className="border border-slate-200 p-3 space-y-2 bg-slate-50/60" data-testid={`training-settings-area-${i}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-400 tabular-nums w-5">{i + 1}.</span>
                <Input value={a.name} onChange={e => setArea(i, { name: e.target.value })}
                       placeholder="Nome area" maxLength={160} className="h-8 rounded-none flex-1 min-w-[180px] bg-white"
                       data-testid={`training-settings-area-${i}-name`} />
                <Input type="number" min="0" step="0.5" value={a.hours ?? ''} onChange={e => setArea(i, { hours: e.target.value })}
                       className="h-8 rounded-none w-20 bg-white" title="Ore"
                       data-testid={`training-settings-area-${i}-hours`} />
                <Select value={a.category || 'tecnica'} onValueChange={v => setArea(i, { category: v })}>
                  <SelectTrigger className="h-8 rounded-none w-28 bg-white text-xs" data-testid={`training-settings-area-${i}-category`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="base">Base</SelectItem>
                    <SelectItem value="tecnica">Tecnica</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                        onClick={() => setCfg(c => ({ ...c, areas: c.areas.filter((_, j) => j !== i) }))}
                        data-testid={`training-settings-area-${i}-remove`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Textarea value={a.description || ''} onChange={e => setArea(i, { description: e.target.value })}
                        rows={2} placeholder="Descrizione (dal manuale cartaceo) — appare al passaggio del mouse"
                        className="rounded-none text-sm bg-white"
                        data-testid={`training-settings-area-${i}-description`} />
            </div>
          ))}
          <Button variant="outline" size="sm" className="h-8 gap-1 rounded-none text-xs"
                  onClick={() => setCfg(c => ({ ...c, areas: [...(c.areas || []), { name: '', hours: 0, category: 'tecnica', description: '' }] }))}
                  data-testid="training-settings-add-area">
            <Plus className="w-3.5 h-3.5" /> Aggiungi area
          </Button>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button size="sm" className="h-9 gap-1.5 rounded-none" onClick={save} disabled={saving} data-testid="training-settings-save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva impostazioni
        </Button>
      </div>

      {showTemplates && <TemplateEditorDialog onClose={() => setShowTemplates(false)} />}
    </div>
  );
}
